import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { isStale } from '../api/problem';
import { placeBasePath } from '../place/place';
import { nameRefusal, type NameSubmitResult } from '../ui/NamePanel';
import type { OrganizationRow } from './use-organizations';

/** Create and rename: each answers the name panel with its result, and says so in a toast. */
export function useOrganizationNames(tenantId: string, reload: () => void) {
  const { t } = useTranslation('administration');
  const navigate = useNavigate();
  const create = async (name: string): Promise<NameSubmitResult> => {
    const { data, error, response } = await apiClient.POST(
      '/v1/administration/tenants/{tenantId}/organizations',
      { params: { path: { tenantId } }, body: { name } },
    );
    if (data === undefined || !response.ok) {
      return nameRefusal(t, error, response.status, 'ORGANIZATION_NAME_TAKEN');
    }
    toast.success(t('organizations.toast.created', { name: data.name }));
    // The new organization has nobody yet: its own page is where people are invited.
    void navigate(placeBasePath({ kind: 'organization', id: data.id }));
    return { ok: true };
  };

  const rename = async (row: OrganizationRow, name: string): Promise<NameSubmitResult> => {
    const { data, error, response } = await apiClient.PATCH(
      '/v1/administration/tenants/{tenantId}/organizations/{organizationId}',
      { params: { path: { tenantId, organizationId: row.id } }, body: { name } },
    );
    if (data === undefined || !response.ok) {
      if (isStale(error)) {
        reload();
      }
      return nameRefusal(t, error, response.status, 'ORGANIZATION_NAME_TAKEN');
    }
    toast.success(t('organizations.toast.renamed', { name: data.name }));
    reload();
    return { ok: true };
  };
  return { create, rename };
}
